import { Table as BaseTable } from 'antd'
import { closestCenter, DndContext } from '@dnd-kit/core'
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers'
import { horizontalListSortingStrategy, SortableContext } from '@dnd-kit/sortable'
import { DragProvider } from './contexts/drag-context'
import { useTableContext } from './contexts/table-context'
import { TableHeaderCell, TableBodyCell } from './components/table-cells'
import { TableDragOverlay } from './components/table-drag-overlay'
import { ITableProps } from './types'
import { useDragAndDrop } from './hooks/use-drag-and-drop'
import { useColumnResize } from './hooks/use-column-resize'
import React from 'react'

function TableWithDrag<T>({ scroll, rowSelection, ...rest }: ITableProps<T>) {
  const { visibleColumns, styles } = useTableContext<T>()
  const { sensors, onDragEnd, onDragOver, columnOrder } = useDragAndDrop<T>(visibleColumns)
  const { columns } = useColumnResize<T>(visibleColumns, columnOrder)

  return (
    <DndContext
      sensors={sensors}
      modifiers={[restrictToHorizontalAxis]}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver(columns)}
      collisionDetection={closestCenter}
    >
      <SortableContext items={columns.map(i => i.key)} strategy={horizontalListSortingStrategy}>
        <BaseTable
          columns={columns}
          className={styles.customTable}
          components={{
            header: { cell: TableHeaderCell },
            body: { cell: TableBodyCell },
          }}
          {...rest}
          scroll={{
            x: 'max-content' as const,
            y: (rest.dataSource?.length as number) > 0 ? 'calc(100vh - 4rem)' : undefined, //FOUC of collapsed headers fix
            ...scroll,
          }}
          rowSelection={rowSelection ? { fixed: 'left', ...rowSelection } : undefined}
        />
      </SortableContext>
      <TableDragOverlay columns={columns} />
    </DndContext>
  )
}

export function Table<T = any>(props: ITableProps<T>) {
  return (
    <DragProvider>
      <TableWithDrag {...props} />
    </DragProvider>
  )
}
